import { CUSTOM_ATTRIBUTE_READ_CAST } from '@provision/attribute-utils';
import { JsonFieldTypeCast } from '@provision/entity-types';
import { CustomAttributeType } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { UIType } from 'src/graphql/entities/custom-view/custom-view.types';
import { ConditionOperator } from 'src/graphql/types';

/**
 * Per-type grid behavior (pure data). Rendering lives in `cell.tsx`, which reads `parseProjectedValue`.
 * Display projects `::text`; `compareCast` applies only on the filter/sort wire path.
 */
export interface CustomAttributeGridBehavior {
  // Existing operator group for the filter UI, or null when not filterable.
  filterUiType: UIType | null;
  // Filter/sort comparison cast; null for display-only types.
  compareCast: JsonFieldTypeCast | null;
  // Subset of customViewOperatorsByUiType[filterUiType].
  operators: ConditionOperator[];
  sortable: boolean;
  filterable: boolean;
}

const DATE_OPERATORS: ConditionOperator[] = [
  ConditionOperator.DateEquals,
  ConditionOperator.DateDoesNotEqual,
  ConditionOperator.DateIsAfter,
  ConditionOperator.DateIsBefore,
  ConditionOperator.DateGreaterOrEqualThan,
  ConditionOperator.DateLessOrEqualThan,
];

// short_text, multi_line_text and url all behave identically in the grid.
const TEXT_LIKE_BEHAVIOR: CustomAttributeGridBehavior = {
  filterUiType: UIType.Text,
  compareCast: CUSTOM_ATTRIBUTE_READ_CAST.short_text,
  operators: [
    ConditionOperator.Contains,
    ConditionOperator.DoesNotContain,
    ConditionOperator.Equals,
    ConditionOperator.DoesNotEqual,
    ConditionOperator.Empty,
    ConditionOperator.NotEmpty,
  ],
  sortable: true,
  filterable: true,
};

export const CUSTOM_ATTRIBUTE_GRID_BEHAVIOR_BY_TYPE: Record<
  CustomAttributeType,
  CustomAttributeGridBehavior
> = {
  short_text: TEXT_LIKE_BEHAVIOR,
  multi_line_text: TEXT_LIKE_BEHAVIOR,
  url: TEXT_LIKE_BEHAVIOR,
  number: {
    filterUiType: UIType.Number,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.number,
    operators: [
      ConditionOperator.Equals,
      ConditionOperator.DoesNotEqual,
      ConditionOperator.Empty,
      ConditionOperator.NotEmpty,
      ConditionOperator.LessThan,
      ConditionOperator.GreaterThan,
    ],
    sortable: true,
    filterable: true,
  },
  checkbox: {
    filterUiType: UIType.List,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.checkbox,
    operators: [ConditionOperator.In, ConditionOperator.NotIn],
    sortable: true,
    filterable: true,
  },
  dropdown: {
    filterUiType: UIType.List,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.dropdown,
    operators: [
      ConditionOperator.In,
      ConditionOperator.NotIn,
      ConditionOperator.Empty,
      ConditionOperator.NotEmpty,
    ],
    sortable: true,
    filterable: true,
  },
  date: {
    filterUiType: UIType.Date,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.date,
    operators: DATE_OPERATORS,
    sortable: true,
    filterable: true,
  },
  date_time: {
    filterUiType: UIType.Date,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.date_time,
    operators: DATE_OPERATORS,
    sortable: true,
    filterable: true,
  },
  multi_select: {
    // List filter UI: In/NotIn are remapped to array-overlap operators on the wire (see the
    // custom-attribute filter serialization); Empty/NotEmpty work against the NULL-when-empty
    // text[] read expression. Not sortable (ordering an array is not meaningful).
    filterUiType: UIType.List,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.multi_select,
    operators: [
      ConditionOperator.In,
      ConditionOperator.NotIn,
      ConditionOperator.Empty,
      ConditionOperator.NotEmpty,
    ],
    sortable: false,
    filterable: true,
  },
  // Unreachable today (hidden from visibleDefinitions); kept for exhaustiveness.
  lookup_relational: {
    filterUiType: null,
    compareCast: CUSTOM_ATTRIBUTE_READ_CAST.lookup_relational,
    operators: [],
    sortable: false,
    filterable: false,
  },
};
